import { Component, OnInit, Inject, ViewChild, ElementRef, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';

import { LocalStorageService } from '@core/services/util/local-storage.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';
import { NgClass } from '@angular/common';
import { CdkDrag } from '@angular/cdk/drag-drop';

declare const Hls: any;
@Component({
    selector: 'app-plugin-video',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './plugin-video.component.html',
    styleUrls: ['./plugin-video.component.scss'],
    standalone: true,
    imports: [MatDialogModule, CdkDrag, NgClass]
})

export class PluginVideoComponent implements OnInit {

    @ViewChild('videoElem') videoElem: ElementRef;

    defaultConfig: any = {
        viewPos: { bottom: '5vh', right: '5vw' },
        muted: 'muted',
        loop: true
    };

    isFullScreen: any = null;
	fullScreenModeOrientation: OrientationType = 'portrait-primary';
    isVideoMuted: boolean = true;
    hasAudio: boolean = false;
    initialPosition: any;

    isVideoDialogOpen: boolean = false;
    supportFullScreen: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialogRef: MatDialogRef<PluginVideoComponent>,
        private cdr: ChangeDetectorRef,
        private localStorage: LocalStorageService,
        private sessionStorage: SessionStorageService
    ) { }

    ngOnInit() {
        const viewPos = this.data.defaultVideoPosition || this.defaultConfig.viewPos;
        const { bottom, right } = viewPos;
        this.dialogRef.updatePosition({ bottom: bottom, right: right });
        this.isFullScreenSupported();
    }

    ngAfterViewInit() {

        const video = this.videoElem.nativeElement;
        const { muted, loop } = this.defaultConfig;
        video.muted = muted;

        //  check if the browser natively supports HLS
        if (typeof Hls !== 'undefined' && Hls.isSupported()) {
            var hls = new Hls();
            hls.attachMedia(video);
            hls.loadSource(this.data.videoSrc);
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = this.data.videoSrc;
        } else {
            this.close();
        }

        // check if the video has audio
        video.addEventListener('loadeddata', () => {
            const videoContainer = (<any>document.getElementById('video-container'))
            if (videoContainer) {
                this.isVideoDialogOpen = true;
                this.playVideo(video);
                const hasSound = video.mozHasAudio || video.webkitAudioDecodedByteCount || (video.audioTracks && video.audioTracks.length);
                this.hasAudio = (hasSound > 0);
                videoContainer.style.display = 'block';
                this.cdr.detectChanges();
            }
        });

        // loop video
        if (typeof video.loop == 'boolean') { // loop supported
            video.loop = loop;
        } else {
            let context = this;
            video.addEventListener('ended', function () {
                context.playVideo(this);
            }, false);
        }

        // fullscreen change handling
        document.addEventListener('fullscreenchange', () => {
            this.isFullScreen = document.fullscreenElement;
            if (!document.fullscreenElement) {
                this._exitFullScreen();
            }
            this.cdr.detectChanges();
        });

        document.addEventListener("visibilitychange",
            this.handleVisibilityChange.bind(this)
            , false);

        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange.bind(this), false);
    }

    isFullScreenSupported() {
        const video = (<any>document.getElementById('video-container'));

        const rfs = video.requestFullscreen
            || video.webkitRequestFullScreen
            || video.mozRequestFullScreen
            || video.msRequestFullscreen;

        const fullScreenSupported = document.fullscreenEnabled ||
            document['webkitFullscreenEnabled'] ||
            document['msFullscreenEnabled'];

        this.supportFullScreen = screen.orientation && rfs && fullScreenSupported;
    }

    requestFullScreen() {
        const video = (<any>document.getElementById('video-container'));

        // screen.orientation.lock(this.fullScreenModeOrientation).then(() => {
        //     console.log('Success');
        // }, (err) => {
        //     console.log(err);
        // });

        const rfs = video.requestFullscreen
            || video.webkitRequestFullScreen
            || video.mozRequestFullScreen
            || video.msRequestFullscreen;

        rfs.call(video)
            .then(() => {
                console.log('Success');
                this._toggleControls(true);
                this.updateVisibilityCount();
            }, (err) => {
                console.log(err);
            });
    }

    _exitFullScreen() {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        this.close('fullscreen');
    }

    private _toggleControls(enable: boolean) {
        const video = this.videoElem.nativeElement;
        video.controls = enable;
        video.muted = false;
    }

    close(state?: string) {
        if (state === 'pip') {
            this.updateVisibilityCount();
        }
        this.sessionStorage.set('isVideoShown', 'true');
        this.isVideoDialogOpen = false;
        this.dialogRef.close();
    }

    toggleVolume() {
        const video = this.videoElem.nativeElement;
        video.muted = !video.muted;
        this.isVideoMuted = video.muted;
    }

    dragStart(e) {
        const rect = e.source.element.nativeElement.getBoundingClientRect();
        this.initialPosition = this.initialPosition || rect;
    }

    dragMoved(e) {
        const div = (<any>document.getElementsByClassName('plugin-video-dialog'));
        const rect = e.source.element.nativeElement.getBoundingClientRect();
        const diff = rect.left - this.initialPosition.left;

        if (rect.top <= 0) {
            div[0].style.transform = `translate(${diff}px, -${this.initialPosition.top}px)`;
        } else if (rect.bottom > window.innerHeight) {
            div[0].style.transform = `translate(${diff}px, ${window.innerHeight - this.initialPosition.bottom}px)`;
        }
    }

    updateVisibilityCount() {
        const videoPlatingUrl = this.localStorage.get('videoPlating') || {};
        videoPlatingUrl[this.data.videoSrc] = videoPlatingUrl[this.data.videoSrc] + 1 || 1;
        this.localStorage.set('videoPlating', videoPlatingUrl);
    }

    handleVisibilityChange() {
        if (this.isVideoDialogOpen) {
            const video = this.videoElem.nativeElement;
            if (document.hidden) {
                video.pause();
            } else {
                this.playVideo(video);
            }
        }
    }

    playVideo(video) {
        let promise = video.play();
        if (promise !== undefined) {
            promise.then(_ => {
            }).catch(error => {
                console.log(error)
            });
        }
    }
}
